import { Box } from '@mui/material';
import {
  getSearchResults_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess_providerSearchResults as SEOSegmentationProviderSearchResult,
  getSearchResults_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess_caregiverSearchResults as SEOSegmentationCaregiverSearchResult,
} from '@/__generated__/getSearchResults';
import { HTL_SLOTS, SEARCH_RESULT_TYPE, ServiceIdForMember, UI_FEATURE_FLAGS } from '@/constants';
import { PageTemplate } from '@/types/common';
import RobustCaregiverCard from './components/RobustCaregiverCard';
import ProviderSearchResult from './ProviderSearchResult';
import RobustProviderCard from './components/RobustProviderCard';
import RobustSmbProviderCard from './components/RobustSmbProviderCard';
import useFeatureFlag from '../hooks/useFeatureFlag';
import { GAMAds } from '../GAMAds';
import { usePageAttributes } from '../contexts/PageAttributesContext';
import { useLDClientFlags } from '../hooks/useLDClientFlags';

interface SearchResultsProps {
  isClientSearchResultsQuery: boolean;
  results: SEOSegmentationCaregiverSearchResult[] | SEOSegmentationProviderSearchResult[];
  resultType: keyof typeof SEARCH_RESULT_TYPE;
  serviceId: string;
  serviceIdForMember: ServiceIdForMember;
}

export interface RenderSearchResultsProps {
  profile: any;
  profileUrl: SEOSegmentationCaregiverSearchResult['profileURL'];
  caregiverReviews: SEOSegmentationCaregiverSearchResult['caregiverReviews'];
  providerReviews: SEOSegmentationProviderSearchResult['providerReviews'];
}

const SearchResults = ({
  isClientSearchResultsQuery,
  results,
  resultType,
  serviceId,
  serviceIdForMember,
}: SearchResultsProps) => {
  const { SEO_3487_GAM_ADS_SEO_MFE: GAMFlag } = useLDClientFlags();
  const { pageTemplate } = usePageAttributes();
  const gamAdIndexPositions = [1, 6, 11];
  const gamAdSlotNames = [
    HTL_SLOTS.SEO_DIRECTORY_INLINE_1,
    HTL_SLOTS.SEO_DIRECTORY_INLINE_2,
    HTL_SLOTS.SEO_DIRECTORY_INLINE_3,
  ];
  const isCaregiverDirectoryPage = pageTemplate === PageTemplate.CAREGIVER_DIRECTORY;
  const showGAMAds = GAMFlag?.variationIndex === 1;

  const isProvider = SEARCH_RESULT_TYPE[resultType] === SEARCH_RESULT_TYPE.PROVIDER;

  const dayCareRobustCardFlag = useFeatureFlag(UI_FEATURE_FLAGS.SEO_3126_DAYCARE_ROBUST_CARDS.id);
  const showRobustProviderCard = dayCareRobustCardFlag?.variationIndex === 1;

  const salcRobustProviderCardFlag = useFeatureFlag(
    UI_FEATURE_FLAGS.SEO_3169_SALC_ROBUST_PROVIDER_CARDS.id
  );

  const showRobustSmbProviderCard =
    salcRobustProviderCardFlag?.variationIndex === 1 ||
    serviceIdForMember === ServiceIdForMember.SENIOR_CARE;

  const renderSearchResults = ({
    profile,
    profileUrl,
    caregiverReviews,
    providerReviews,
  }: RenderSearchResultsProps) => {
    if (showRobustProviderCard && isProvider) {
      return (
        <RobustProviderCard
          seoSegmentationProvider={profile.seoSegmentationProvider}
          isClientSearchResultsQuery={isClientSearchResultsQuery}
          serviceId={serviceId}
          providerReviews={providerReviews}
          url={profileUrl}
          philosophies={profile.philosophies}
          schoolTypes={profile.schoolTypes}
          licenseVerified={profile.licenseVerified}
        />
      );
    }

    if (isProvider && showRobustSmbProviderCard) {
      return (
        <RobustSmbProviderCard
          seoSegmentationProvider={profile.seoSegmentationProvider}
          isClientSearchResultsQuery={isClientSearchResultsQuery}
          providerReviews={providerReviews}
          url={profileUrl}
          seniorAssistedLivingCenterAttributes={profile.seniorAssistedLivingCenterAttributes}
          licenseVerified={profile.licenseVerified}
        />
      );
    }

    if (isProvider) {
      return (
        <ProviderSearchResult
          seoSegmentationProvider={profile.seoSegmentationProvider}
          isClientSearchResultsQuery={isClientSearchResultsQuery}
          serviceId={serviceId}
          providerReviews={providerReviews}
          url={profileUrl}
        />
      );
    }

    return (
      <RobustCaregiverCard
        caregiver={profile.caregiver}
        isClientSearchResultsQuery={isClientSearchResultsQuery}
        caregiverReviews={caregiverReviews}
        url={profileUrl}
      />
    );
  };

  const mappedResults = results.map((profile: any) => {
    const profileUrl = profile.profileURL || null;
    const caregiverReviews = profile?.caregiverReviews ?? [];
    const providerReviews = profile?.providerReviews ?? [];
    return (
      <Box key={profileUrl}>
        {renderSearchResults({ profile, profileUrl, caregiverReviews, providerReviews })}
      </Box>
    );
  });

  if (showGAMAds && isCaregiverDirectoryPage && !isProvider) {
    const insertElementsAtPositions = (
      array: JSX.Element[],
      elements: JSX.Element[],
      positions: number[]
    ) => {
      const newList = [...array];
      positions.forEach((position, index) => {
        // only insert if the position is less than the length of the array
        if (position <= newList.length) {
          newList.splice(position + index, 0, elements[index]);
        }
      });

      return newList;
    };

    const gamAds: JSX.Element[] = gamAdSlotNames.map((slotName) => (
      <GAMAds key={slotName} slotName={slotName} />
    ));

    const listingWithAds = insertElementsAtPositions(mappedResults, gamAds, gamAdIndexPositions);

    return <Box>{listingWithAds}</Box>;
  }

  return <Box>{mappedResults}</Box>;
};

export default SearchResults;
